import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  OnInit,
  Renderer2,
  TemplateRef,
  ViewChild,
  ViewContainerRef,
} from '@angular/core';
import { FieldType, FormlyFieldConfig } from '@ngx-formly/core';
import { FlexibleConnectedPositionStrategy, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { TemplatePortal } from '@angular/cdk/portal';
import { combineLatest, debounceTime, distinctUntilChanged, Observable, Subscription } from 'rxjs';
import { FormControl } from '@angular/forms';
import { map } from 'rxjs/operators';
import { filterAutocompleteItems } from '@app/forms/formly/types/input-field-with-autocomplete/input-field-with-autocomplete.helper';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { IconName, PopupsService } from '@voyagu/portal-design-system';

export interface AutocompleteOption {
  title: string;
  value: string;
  suffix?: string;
  icons?: string[];
}

export interface AutocompleteItem {
  options: AutocompleteOption[];
  groupData?: AutocompleteOption;
}

interface AutoCompleteProps {
  autocompleteOptions$: Observable<AutocompleteItem[]>;
  prefixIcon: string;
  placeholder: string;
  maxHeight: number;
}

interface AutoCompleteFormlyFieldConfig extends FormlyFieldConfig {
  props?: AutoCompleteProps;
  formControl: FormControl<string>;
}

@Component({
  selector: 'input-field-with-autocomplete',
  templateUrl: './input-field-with-autocomplete.component.html',
  styleUrls: ['./input-field-with-autocomplete.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class InputFieldWithAutocompleteComponent
  extends FieldType<AutoCompleteFormlyFieldConfig>
  implements OnInit
{
  filteredOptions$: Observable<AutocompleteItem[]>;
  overlayBackdropSubscription: Subscription;
  overlayRef: OverlayRef | null;
  isSelected = false;
  heights: number[] = [];
  dropdownHeight: number = 0;
  isScrollable = false;

  readonly maxDropdownHeight = 255;

  get leadingIcon(): IconName | undefined {
    if (this.props.prefixIcon === 'flight_takeoff') {
      return 'flightTakeoff';
    }

    if (this.props.prefixIcon === 'flight_land') {
      return 'flightLand';
    }

    return undefined;
  }

  @ViewChild('dropdownTemplate') dropdownTemplate: TemplateRef<any>;
  @ViewChild('inputElement', { read: ElementRef }) inputElement: ElementRef;
  @ViewChild('mobileTemplate') mobileTemplate: TemplateRef<unknown>;

  constructor(
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private renderer: Renderer2,
    private popupsService: PopupsService,
    private dr: DestroyRef,
  ) {
    super();
  }

  ngOnInit(): void {
    this.filteredOptions$ = combineLatest([
      this.formControl.valueChanges.pipe(
        distinctUntilChanged((prev, curr) => prev === curr && !this.isSelected),
        debounceTime(125),
      ),
      this.props.autocompleteOptions$,
    ]).pipe(
      map(([filterValue, items]) => {
        const preparedValue = filterValue.toLowerCase().trim();
        const filteredItems = filterAutocompleteItems(preparedValue, items);
        this.heights = filteredItems.map((item) => this.getGroupHeight(item));
        this.dropdownHeight = this.getDropdownHeight(this.heights);
        this.isScrollable = this.setIsScrollable(this.heights);

        if (this.isSelected) {
          this.isSelected = false;
          this.closeDropdown();
          return filteredItems;
        }

        if (preparedValue && !this.overlayRef) {
          this.openDropdown();
        }

        if (!preparedValue && this.overlayRef) {
          this.closeDropdown();
        }

        return filteredItems;
      }),
    );
  }

  selectOption(option: AutocompleteOption): void {
    this.isSelected = true;
    this.formControl.setValue(option.value);
  }

  openDropdown(): void {
    const positionStrategy = this.overlay
      .position()
      .flexibleConnectedTo(this.inputElement)
      .withPositions([{ originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top' }]);

    this.overlayRef = this.overlay.create({
      positionStrategy,
      hasBackdrop: true,
      backdropClass: 'cdk-overlay-transparent-backdrop',
    }) as OverlayRef;

    const portal = new TemplatePortal(this.dropdownTemplate, this.viewContainerRef);
    this.overlayRef.attach(portal);

    if (this.overlayBackdropSubscription) {
      this.overlayBackdropSubscription.unsubscribe();
    }

    this.overlayBackdropSubscription = this.overlayRef
      .backdropClick()
      .pipe(takeUntilDestroyed(this.dr))
      .subscribe(() => {
        this.closeDropdown();
      });

    const dropdownElement = this.overlayRef.overlayElement.querySelector(
      '.dropdown',
    ) as HTMLElement;
    if (dropdownElement) {
      this.renderer.setStyle(
        dropdownElement,
        'width',
        `${(this.inputElement.nativeElement as HTMLElement).offsetWidth}px`,
      );
    }
  }

  closeDropdown(): void {
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }

  onInputClick(): void {
    if (this.formControl.value && !this.overlayRef) {
      this.openDropdown();
    }
  }

  showMobileModal() {
    this.popupsService.showModalWithFullHeightMobile(this.mobileTemplate, 0);
  }

  hideModal() {
    this.popupsService.hideLast();
  }

  trackByItemFn(index: number, option: AutocompleteItem): string {
    return option.options.map((item) => item.value).join('');
  }

  trackByOptionFn(option: AutocompleteOption): string {
    return option.value;
  }

  updateOverlayPosition(): void {
    if (this.overlayRef) {
      const positionStrategy = this.overlayRef.getConfig()
        .positionStrategy as FlexibleConnectedPositionStrategy;
      positionStrategy.apply();
    }
  }

  getGroupHeight(item: AutocompleteItem): number {
    const itemHeight = 36;
    const offsetHeight = 4;
    const paddingHeight = 4;
    const borderHeight = 0.8;
    const headerHeight = paddingHeight * 2 + itemHeight + borderHeight;

    if (item.options.length === 1) {
      return headerHeight;
    }

    return headerHeight + item.options.length * itemHeight + item.options.length * offsetHeight;
  }

  getDropdownHeight(items: number[]): number {
    const height = items.reduce((acc, curr) => acc + curr, 0) - 1;
    return Math.min(height, this.maxDropdownHeight);
  }

  setIsScrollable(items: number[]): boolean {
    const height = items.reduce((acc, curr) => acc + curr, 0) - 1;
    return height > this.maxDropdownHeight;
  }
}
